import { createSlice } from "@reduxjs/toolkit";

const safeGetFromStorage = (storage, key, parse = false) => {
  try {
    const value = storage.getItem(key);
    return parse ? JSON.parse(value) : value;
  } catch {
    return null;
  }
};

const safeSetToStorage = (storage, key, value) => {
  try {
    storage.setItem(key, value);
  } catch {
    // console.warn(`Failed to set key "${key}" in storage.`);
  }
};

const safeRemoveFromStorage = (storage, key) => {
  try {
    storage.removeItem(key);
  } catch {
    // console.warn(`Failed to remove key "${key}" from storage.`);
  }
};

const initialState = {
  statusLoading: false,
  accessToken:
    safeGetFromStorage(localStorage, "accessToken") ||
    safeGetFromStorage(sessionStorage, "accessToken"),
  refreshToken:
    safeGetFromStorage(localStorage, "refreshToken") ||
    safeGetFromStorage(sessionStorage, "refreshToken"),
  userData: safeGetFromStorage(localStorage, "userData", true),
  imageTypesData: [],
  hideWidget: false,
  unreadCount: 0,
  questHistoryCount: 0,
};

export const commonSlice = createSlice({
  name: "common",
  initialState,
  reducers: {
    setStatusLoading: (state, action) => {
      state.statusLoading = action.payload;
    },

    setAccessToken: (state, action) => {
      state.accessToken = action.payload;
      if (action.payload) {
        // Check which storage has the token to maintain consistency
        const existingInLocal = localStorage.getItem("accessToken");
        const existingInSession = sessionStorage.getItem("accessToken");

        if (existingInLocal) {
          safeSetToStorage(localStorage, "accessToken", action.payload);
        } else if (existingInSession) {
          safeSetToStorage(sessionStorage, "accessToken", action.payload);
        } else {
          // Default to localStorage if no existing token
          safeSetToStorage(localStorage, "accessToken", action.payload);
        }
      } else {
        safeRemoveFromStorage(localStorage, "accessToken");
        safeRemoveFromStorage(sessionStorage, "accessToken");
      }
    },

    setRefreshToken: (state, action) => {
      state.refreshToken = action.payload;
      if (action.payload) {
        // Check which storage has the token to maintain consistency
        const existingInLocal = localStorage.getItem("refreshToken");
        const existingInSession = sessionStorage.getItem("refreshToken");

        if (existingInLocal) {
          safeSetToStorage(localStorage, "refreshToken", action.payload);
        } else if (existingInSession) {
          safeSetToStorage(sessionStorage, "refreshToken", action.payload);
        } else {
          // Default to localStorage if no existing token
          safeSetToStorage(localStorage, "refreshToken", action.payload);
        }
      } else {
        safeRemoveFromStorage(localStorage, "refreshToken");
        safeRemoveFromStorage(sessionStorage, "refreshToken");
      }
    },

    setUserData: (state, action) => {
      state.userData = action.payload;
      if (action.payload) {
        safeSetToStorage(localStorage, "userData", JSON.stringify(action.payload));
      } else {
        safeRemoveFromStorage(localStorage, "userData");
      }
    },

    clearAccessToken: (state) => {
      state.accessToken = null;
      state.refreshToken = null;
      state.userData = null;
      safeRemoveFromStorage(localStorage, "accessToken");
      safeRemoveFromStorage(sessionStorage, "accessToken");
      safeRemoveFromStorage(localStorage, "refreshToken");
      safeRemoveFromStorage(sessionStorage, "refreshToken");
      safeRemoveFromStorage(localStorage, "userData");
    },

    setImageTypesData: (state, action) => {
      state.imageTypesData = action.payload || [];
    },

    clearImageTypesData: (state) => {
      state.imageTypesData = [];
    },

    setHideWidget: (state, action) => {
      state.hideWidget = action.payload;
    },

    setUnreadCount: (state, action) => {
      state.unreadCount = action.payload;
    },

    setQuestHistoryCount: (state, action) => {
      state.questHistoryCount = action.payload;
    },
  },
});

export const {
  setStatusLoading,
  setAccessToken,
  setRefreshToken,
  setUserData,
  clearAccessToken,
  setImageTypesData,
  clearImageTypesData,
  setHideWidget,
  setUnreadCount,
  setQuestHistoryCount,
} = commonSlice.actions;

export default commonSlice.reducer;
